import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import vars from '@exo/narrative-tracker-variables';

// ----- Animations ----
var topRightLoader = keyframes(["0%,100%{transform:rotate3d(1,0,0,90deg);opacity:0;}10%{transform:rotate3d(0,0,0,0);opacity:1;}40%{transform:rotate3d(0,0,0,0);opacity:1;}60%{transform:rotate3d(0,0,0,0);opacity:1;}70%{transform:rotate3d(0,1,0,90deg);opacity:0;}99.9%{transform:rotate3d(0,1,0,90deg);opacity:0;}}"]);
var bottomRightLoader = keyframes(["0%,100%{transform:rotate3d(1,0,0,90deg);opacity:0;}10%{transform:rotate3d(1,0,0,90deg);opacity:0;}20%{transform:rotate3d(0,0,0,0);opacity:1;}70%{transform:rotate3d(0,0,0,0);opacity:1;}80%{transform:rotate3d(0,1,0,90deg);opacity:0;}99.9%{transform:rotate3d(0,1,0,90deg);opacity:0;}"]);
var topLeftLoader = keyframes(["0%,100%{transform:rotate3d(0,1,0,90deg);opacity:0;}20%{transform:rotate3d(0,1,0,90deg);opacity:0;}30%{transform:rotate3d(0,0,0,0);opacity:1;}80%{transform:rotate3d(0,0,0,0);opacity:1;}90%{transform:rotate3d(1,0,0,90deg);opacity:0;}99.9%{transform:rotate3d(1,0,0,90deg);opacity:0;}"]);
var bottomLeftLoader = keyframes(["0%,100%{transform:rotate3d(0,1,0,90deg);opacity:0;}30%{transform:rotate3d(0,1,0,90deg);opacity:0;}40%{transform:rotate3d(0,0,0,0);opacity:1;}90%{transform:rotate3d(0,0,0,0);opacity:1;}99.9%{transform:rotate3d(1,0,0,90deg);opacity:0;}"]);

// ----- Styled components ---
var Spinner = styled.div.withConfig({
	displayName: "LoadingSpinner__Spinner",
	componentId: "sc-krzjyu-0"
})(["position:absolute;height:", ";width:", ";"], function (props) {
	return props.height || "100%";
}, function (props) {
	return props.width || "100%";
});

var TopLeftBox = styled.div.withConfig({
	displayName: "LoadingSpinner__TopLeftBox",
	componentId: "sc-krzjyu-1"
})(["position:absolute;height:50%;width:50%;top:0;left:0;"]);
var TopRightBox = styled.div.withConfig({
	displayName: "LoadingSpinner__TopRightBox",
	componentId: "sc-krzjyu-2"
})(["position:absolute;height:50%;width:50%;top:0;right:0;"]);
var BottomRightBox = styled.div.withConfig({
	displayName: "LoadingSpinner__BottomRightBox",
	componentId: "sc-krzjyu-3"
})(["position:absolute;height:50%;width:50%;bottom:0;right:0;"]);
var BottomLeftBox = styled.div.withConfig({
	displayName: "LoadingSpinner__BottomLeftBox",
	componentId: "sc-krzjyu-4"
})(["position:absolute;height:50%;width:50%;bottom:0;left:0;"]);
var TopLeft = styled.svg.withConfig({
	displayName: "LoadingSpinner__TopLeft",
	componentId: "sc-krzjyu-5"
})(["fill:#8cc94e;transform-style:preserve-3D;transform-origin:top right;opacity:1;animation:", " 2500ms infinite ease-out;"], topLeftLoader);
var TopRight = styled.svg.withConfig({
	displayName: "LoadingSpinner__TopRight",
	componentId: "sc-krzjyu-6"
})(["fill:#bcde87;transform-style:preserve-3D;transform-origin:bottom left;opacity:1;animation:", " 2500ms infinite ease-out;"], topRightLoader);
var BottomRight = styled.svg.withConfig({
	displayName: "LoadingSpinner__BottomRight",
	componentId: "sc-krzjyu-7"
})(["fill:#123984;transform-style:preserve-3D;transform-origin:top left;opacity:1;animation:", " 2500ms infinite ease-out;"], bottomRightLoader);
var BottomLeft = styled.svg.withConfig({
	displayName: "LoadingSpinner__BottomLeft",
	componentId: "sc-krzjyu-8"
})(["fill:#2e6fbd;transform-style:preserve-3D;transform-origin:bottom right;opacity:1;animation:", " 2500ms infinite ease-out;"], bottomLeftLoader);

var LoadingSpinner = function LoadingSpinner(props) {
	return React.createElement(
		Spinner,
		{ height: props.height, width: props.width },
		React.createElement(
			TopLeftBox,
			null,
			React.createElement(
				TopLeft,
				{
					viewBox: "0 0 240 240",
					version: "1.1",
					xmlns: "http://www.w3.org/2000/svg",
					x: "0px",
					y: "0px"
				},
				React.createElement("polygon", { points: "0,0 240,240 240,0 \t" })
			)
		),
		React.createElement(
			TopRightBox,
			null,
			React.createElement(
				TopRight,
				{
					viewBox: "0 0 240 240",
					version: "1.1",
					xmlns: "http://www.w3.org/2000/svg",
					x: "0px",
					y: "0px"
				},
				React.createElement("polygon", { points: "0,0 240,240 0,240 " })
			)
		),
		React.createElement(
			BottomRightBox,
			null,
			React.createElement(
				BottomRight,
				{
					viewBox: "0 0 240 240",
					version: "1.1",
					xmlns: "http://www.w3.org/2000/svg",
					x: "0px",
					y: "0px"
				},
				React.createElement("polygon", { points: "0,0 0,240 240,0 \t" })
			)
		),
		React.createElement(
			BottomLeftBox,
			null,
			React.createElement(
				BottomLeft,
				{
					viewBox: "0 0 240 240",
					version: "1.1",
					xmlns: "http://www.w3.org/2000/svg",
					x: "0px",
					y: "0px"
				},
				React.createElement("polygon", { points: "240,240 0,240 240,0 \t" })
			)
		)
	);
};

LoadingSpinner.defaultProps = {
	height: "",
	width: ""
};

LoadingSpinner.propTypes = {
	height: PropTypes.string,
	width: PropTypes.string
};

var PageLoader = styled.div.withConfig({
	displayName: "WholePageLoader__PageLoader",
	componentId: "sc-xa7msc-0"
})(["position:absolute;top:0;bottom:0;left:0;right:0;background-color:", ";"], function (props) {
	return props.darkUI ? vars.black_2 : vars.blueGrey_4;
});
var SpinnerBox = styled.div.withConfig({
	displayName: "WholePageLoader__SpinnerBox",
	componentId: "sc-xa7msc-1"
})(["position:absolute;height:", ";width:", ";top:50%;left:50%;margin:-50px 0 0 -50px;overflow:hidden;"], function (props) {
	return props.height || "100px";
}, function (props) {
	return props.width || "100px";
});

var WholePageLoader = function WholePageLoader(props) {
	return React.createElement(
		PageLoader,
		{ darkUI: props.darkUI },
		React.createElement(
			SpinnerBox,
			{ height: props.height, width: props.width },
			React.createElement(LoadingSpinner, null)
		)
	);
};

WholePageLoader.defaultProps = {
	darkUI: true,
	height: "",
	width: ""
};

WholePageLoader.propTypes = {
	darkUI: PropTypes.bool,
	height: PropTypes.string,
	width: PropTypes.string
};

export { LoadingSpinner, WholePageLoader };
//# sourceMappingURL=index.es.js.map
